import React, { useState, useEffect } from "react";
import { useLanguage } from "../utils/LanguageContext";
import { FaGithub, FaLinkedin, FaAngellist, FaArrowUp } from "react-icons/fa";

const Footer = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggle = () => setIsVisible(window.scrollY > 300);
    window.addEventListener("scroll", toggle, { passive: true });
    return () => window.removeEventListener("scroll", toggle);
  }, []);

  const socialLinks = [
    { id: 1, icon: <FaGithub />, url: "https://github.com/mwafrika", label: "GitHub" },
    { id: 2, icon: <FaLinkedin />, url: "https://www.linkedin.com/in/mwafrika-mufungizi", label: "LinkedIn" },
    { id: 3, icon: <FaAngellist />, url: "https://angel.co/u/mwafrika-mufungizi", label: "AngelList" },
  ];

  const navLinks =
    language === "en"
      ? [
          { name: "Home", url: "/#home" },
          { name: "About", url: "/#about" },
          { name: "Projects", url: "/#projects" },
          { name: "Skills", url: "/#tech" },
          { name: "Experience", url: "/#experience" },
          { name: "Contact", url: "/#contact" },
        ]
      : [
          { name: "Accueil", url: "/#home" },
          { name: "À propos", url: "/#about" },
          { name: "Projets", url: "/#projects" },
          { name: "Compétences", url: "/#tech" },
          { name: "Expérience", url: "/#experience" },
          { name: "Contact", url: "/#contact" },
        ];

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="w-full bg-slate-900 dark:bg-slate-50 border-t border-slate-800 dark:border-slate-200">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

          {/* Brand */}
          <div className="flex flex-col gap-4">
            <h3 className="text-white dark:text-slate-900 text-lg font-bold tracking-tight">
              <span className="bg-gradient-to-r from-violet-400 to-sky-400 bg-clip-text text-transparent">MW</span>
              <span>afrika Josué</span>
            </h3>
            <p className="text-gray-400 dark:text-slate-600 text-sm max-w-xs leading-relaxed">
              {language === "en"
                ? "Full Stack Developer crafting scalable web applications and APIs with modern technologies."
                : "Développeur Full Stack créant des applications web et APIs évolutives avec des technologies modernes."}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {socialLinks.map(({ id, icon, url, label }) => (
                <a
                  key={id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="social-icon w-9 h-9 flex items-center justify-center text-gray-400 dark:text-slate-500 hover:text-violet-400 dark:hover:text-violet-600 bg-slate-800 dark:bg-slate-100 hover:bg-slate-700 dark:hover:bg-slate-200 rounded-xl text-base transition-colors duration-200"
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="flex flex-col gap-3">
            <h4 className="text-white dark:text-slate-900 font-semibold text-xs uppercase tracking-widest">
              {language === "en" ? "Quick Links" : "Liens rapides"}
            </h4>
            <nav aria-label="Footer navigation">
              <ul className="flex flex-col gap-1.5">
                {navLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.url}
                      className="footer-link text-gray-400 dark:text-slate-600 hover:text-violet-400 dark:hover:text-violet-600 text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-3">
            <h4 className="text-white dark:text-slate-900 font-semibold text-xs uppercase tracking-widest">
              {language === "en" ? "Get in touch" : "Me contacter"}
            </h4>
            <p className="text-gray-400 dark:text-slate-600 text-sm leading-relaxed">
              {language === "en"
                ? "Open to freelance and full-time opportunities. Let's build something great."
                : "Ouvert aux missions freelance et aux postes à temps plein. Construisons quelque chose."}
            </p>
            <a
              href="/#contact"
              className="btn-press inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-sky-500 text-white text-sm font-medium w-fit"
            >
              {language === "en" ? "Contact Me" : "Me contacter"}
            </a>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-slate-800 dark:border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 dark:text-slate-500 text-xs text-center sm:text-left">
            {`© ${new Date().getFullYear()} Mwafrika Josué. ${
              language === "en" ? "All rights reserved." : "Tous droits réservés."
            }`}
          </p>
          <button
            type="button"
            onClick={scrollToTop}
            aria-label={language === "en" ? "Back to top" : "Retour en haut"}
            className="flex items-center gap-2 text-gray-500 dark:text-slate-500 hover:text-violet-400 dark:hover:text-violet-600 text-xs transition-colors duration-200 group"
          >
            {language === "en" ? "Back to top" : "Retour en haut"}
            <span className="social-icon w-6 h-6 flex items-center justify-center rounded-lg bg-slate-800 dark:bg-slate-100 group-hover:bg-slate-700 dark:group-hover:bg-slate-200 transition-colors duration-200">
              <FaArrowUp className="text-[10px]" aria-hidden="true" />
            </span>
          </button>
        </div>
      </div>

      {/* Floating back-to-top FAB */}
      <div
        className={`fixed bottom-8 right-8 z-[999] transition-all duration-500 ${
          isVisible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-8 pointer-events-none"
        }`}
      >
        <button
          type="button"
          onClick={scrollToTop}
          aria-label={language === "en" ? "Scroll to top" : "Retourner en haut"}
          className="animate-float-bounce btn-press w-12 h-12 rounded-full bg-gradient-to-tr from-violet-600 to-sky-500 text-white shadow-2xl shadow-violet-500/30 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 flex items-center justify-center"
        >
          <FaArrowUp className="text-base" aria-hidden="true" />
        </button>
      </div>
    </footer>
  );
};

export default Footer;
